import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Crown, Calendar } from "lucide-react";
import { ResultRow, getGrupoFromMilhar, animalNames, animalEmojis } from "./constants";

interface BuscadorNumerosProps {
  results: ResultRow[];
}

interface Aparicao {
  data: string;
  turno: string;
  posicao: number;
  milhar: string;
}

const AparicoesList: React.FC<{ titulo: string; aparicoes: Aparicao[]; cor: string }> = ({ titulo, aparicoes, cor }) => {
  if (aparicoes.length === 0) return null;
  return (
    <div className="bg-black/30 rounded-lg p-2 sm:p-3">
      <p className="text-[10px] sm:text-xs font-semibold text-yellow-400/80 mb-1.5 flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        Últimas aparições — {titulo}
      </p>
      <div className="flex flex-wrap gap-1 sm:gap-1.5">
        {aparicoes.map((u, i) => (
          <Badge key={i} variant="outline" className={`text-[9px] sm:text-[10px] font-mono border-${cor}/30 bg-${cor}/10 px-1.5 py-0.5`}>
            📅 {u.data} • {u.turno} • {u.posicao}º • <span className="text-yellow-300">{u.milhar}</span>
          </Badge>
        ))}
      </div>
    </div>
  );
};

const BuscadorNumeros: React.FC<BuscadorNumerosProps> = ({ results }) => {
  const [query, setQuery] = useState("");

  const searchResults = useMemo(() => {
    const q = query.trim();
    if (!q || q.length === 0 || !/^\d+$/.test(q)) return null;

    // Milhar exata (4 dígitos)
    const milharExata = results.filter(r => r.milhar === q.padStart(4, '0'));

    // Centena (últimos 3 dígitos)
    const centenaVal = q.slice(-3).padStart(3, '0');
    const centena = q.length >= 3
      ? results.filter(r => r.milhar.slice(-3) === centenaVal)
      : [];

    // Dezena (últimos 2 dígitos)
    const dezenaVal = q.slice(-2).padStart(2, '0');
    const dezena = q.length >= 2
      ? results.filter(r => r.milhar.slice(-2) === dezenaVal)
      : [];

    // Grupo correspondente à dezena
    const dezNum = parseInt(q.slice(-2));
    const grupo = dezNum > 0 ? Math.floor((dezNum - 1) / 4) + 1 : null;
    const grupoResults = grupo ? results.filter(r => r.grupo === grupo) : [];

    // Atraso
    const calcAtraso = (matches: ResultRow[]) => {
      if (matches.length === 0) return results.length;
      const lastDate = matches.reduce((latest, r) => r.data > latest ? r.data : latest, '');
      const idx = results.findIndex(r => r.data === lastDate && matches.some(m => m.milhar === r.milhar && m.turno === r.turno && m.posicao === r.posicao));
      return idx >= 0 ? idx : 0;
    };

    const toAparicoes = (arr: ResultRow[], limit = 10): Aparicao[] =>
      arr.slice(0, limit).map(r => ({ data: r.data, turno: r.turno, posicao: r.posicao, milhar: r.milhar }));

    return {
      query: q,
      milhar: { count: milharExata.length, atraso: calcAtraso(milharExata), ultimas: toAparicoes(milharExata) },
      centena: { count: centena.length, atraso: calcAtraso(centena), ultimas: toAparicoes(centena) },
      dezena: { count: dezena.length, atraso: calcAtraso(dezena), ultimas: toAparicoes(dezena) },
      grupo: grupo ? { numero: grupo, nome: animalNames[grupo] || '?', emoji: animalEmojis[grupo] || '', count: grupoResults.length, ultimas: toAparicoes(grupoResults) } : null,
    };
  }, [query, results]);

  return (
    <Card className="border-yellow-500/50 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-lg shadow-yellow-500/10">
      <CardHeader className="pb-2 sm:pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-yellow-400">
          <Crown className="h-5 w-5 text-yellow-400" />
          Buscador de Números
          <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 text-[10px]">PRO</Badge>
        </CardTitle>
        <p className="text-[10px] sm:text-xs text-yellow-200/60">
          Digite um número para ver frequência, atraso e histórico em todas as modalidades
        </p>
      </CardHeader>
      <CardContent className="space-y-3 sm:space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-yellow-500/60" />
          <Input
            type="text"
            inputMode="numeric"
            placeholder="Ex: 1234, 234, 34, 4"
            value={query}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 4);
              setQuery(v);
            }}
            className="text-base sm:text-lg font-mono text-center tracking-widest pl-9 border-yellow-500/40 bg-black/40 text-yellow-100 placeholder:text-yellow-200/30 focus-visible:ring-yellow-500/50"
            maxLength={4}
          />
        </div>

        {searchResults && (
          <div className="space-y-3 sm:space-y-4">
            {/* Cards grid */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
              {/* Milhar */}
              <Card className="bg-black/40 border-yellow-500/30">
                <CardContent className="p-2 sm:p-3 text-center">
                  <p className="text-[10px] sm:text-xs text-yellow-200/60 mb-0.5">Milhar</p>
                  <p className="text-xl sm:text-2xl font-bold font-mono text-yellow-400">
                    {searchResults.query.padStart(4, '0')}
                  </p>
                  <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 mt-1 text-[10px]">
                    {searchResults.milhar.count}x
                  </Badge>
                  <p className="text-[9px] sm:text-[10px] text-yellow-200/50 mt-0.5">
                    Atraso: {searchResults.milhar.atraso}
                  </p>
                </CardContent>
              </Card>

              {/* Centena */}
              {searchResults.query.length >= 3 && (
                <Card className="bg-black/40 border-yellow-600/30">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-[10px] sm:text-xs text-yellow-200/60 mb-0.5">Centena</p>
                    <p className="text-xl sm:text-2xl font-bold font-mono text-yellow-500">
                      {searchResults.query.slice(-3).padStart(3, '0')}
                    </p>
                    <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 mt-1 text-[10px]">
                      {searchResults.centena.count}x
                    </Badge>
                    <p className="text-[9px] sm:text-[10px] text-yellow-200/50 mt-0.5">
                      Atraso: {searchResults.centena.atraso}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Dezena */}
              {searchResults.query.length >= 2 && (
                <Card className="bg-black/40 border-yellow-600/30">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-[10px] sm:text-xs text-yellow-200/60 mb-0.5">Dezena</p>
                    <p className="text-xl sm:text-2xl font-bold font-mono text-green-400">
                      {searchResults.query.slice(-2).padStart(2, '0')}
                    </p>
                    <Badge className="bg-green-500/20 text-green-300 border-green-500/30 mt-1 text-[10px]">
                      {searchResults.dezena.count}x
                    </Badge>
                    <p className="text-[9px] sm:text-[10px] text-yellow-200/50 mt-0.5">
                      Atraso: {searchResults.dezena.atraso}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Grupo */}
              {searchResults.grupo && (
                <Card className="bg-black/40 border-orange-500/30">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-[10px] sm:text-xs text-yellow-200/60 mb-0.5">Grupo</p>
                    <p className="text-lg sm:text-xl font-bold">
                      {searchResults.grupo.emoji} G{String(searchResults.grupo.numero).padStart(2, '0')}
                    </p>
                    <p className="text-[10px] text-yellow-200/60">{searchResults.grupo.nome}</p>
                    <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 mt-1 text-[10px]">
                      {searchResults.grupo.count}x
                    </Badge>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Histórico de aparições por modalidade */}
            <div className="space-y-2">
              <AparicoesList titulo={`Milhar ${searchResults.query.padStart(4, '0')}`} aparicoes={searchResults.milhar.ultimas} cor="yellow-500" />
              {searchResults.query.length >= 3 && (
                <AparicoesList titulo={`Centena ${searchResults.query.slice(-3).padStart(3, '0')}`} aparicoes={searchResults.centena.ultimas} cor="yellow-600" />
              )}
              {searchResults.query.length >= 2 && (
                <AparicoesList titulo={`Dezena ${searchResults.query.slice(-2).padStart(2, '0')}`} aparicoes={searchResults.dezena.ultimas} cor="green-500" />
              )}
              {searchResults.grupo && (
                <AparicoesList titulo={`Grupo ${searchResults.grupo.emoji} ${searchResults.grupo.nome}`} aparicoes={searchResults.grupo.ultimas} cor="orange-500" />
              )}
            </div>

            {searchResults.milhar.count === 0 && searchResults.dezena.count === 0 && (
              <p className="text-center text-sm text-yellow-200/50">
                Nenhuma ocorrência encontrada no período carregado.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BuscadorNumeros;
